/**
 * useDismissibleLayer — the one contract for "close this layer when the user
 * clicks outside it or presses Escape" (#335).
 *
 * Before this hook, 11 files registered their own global dismissal listeners and
 * disagreed on every axis: `mousedown` vs `pointerdown`, `document` vs `window`,
 * bubble vs capture, and whether the listener was gated on the open state at
 * all. Three components (`Dropdown`, `MultiSelect`, `SearchInput`) registered
 * their outside-click listener unconditionally, so it stayed live while the
 * layer was closed. Those three migrated here in #335; #336 then migrated the
 * four trading surfaces (AlertControlsMenu, DashboardToolbar, OrderActionsMenu,
 * TradesTable). Five files still hand-roll their own — Modal, EditPositionModal,
 * cardKit's InfoTooltip, PositionSidebar and DecreaseOrderQuantityEditor — and
 * are named by neither ticket, so they stay as they are.
 *
 * ─── Two deliberate choices, both load-bearing ────────────────────────────────
 *
 * 1. `mousedown`, NOT `pointerdown`. A portal-rendered menu can only defend
 *    itself from an ancestor's outside-click listener by stopping propagation
 *    from its own React handler — and `Dropdown`'s menu does exactly that with
 *    `onMouseDown`. `pointerdown` fires earlier and would sail straight past
 *    that guard, silently dismissing the parent layer. Measured, not assumed:
 *    picking a value in `SearchInput`'s filter dropdown keeps the filters panel
 *    open today only because both sides agree on `mousedown`.
 *
 * 2. Bubble phase, NOT capture. Same reason, and stronger: a capture-phase
 *    listener on `document` runs BEFORE any handler on `document.body`, so the
 *    portal's `stopPropagation` could never run in time. Capture would break
 *    every nested layer in the app without failing a single existing test.
 *
 * Capture phase, `pointerdown`, and exempt selectors (see `PositionSidebar`'s
 * `[data-position-toggle]`) are still deliberately absent — add an option when
 * a real caller needs one, not before. #336 added the two that had real callers:
 * the dismissal reason and `dismissOnFocusOut`. An in-flight gate needed no
 * option at all: `OrderActionsMenu` expresses it as `open: isOpen && !busy`,
 * which removes the listeners while busy rather than early-returning from them.
 *
 * ─── Why `focusout` exists, and why Escape stays unlayered ────────────────────
 *
 * `dismissOnFocusOut` has exactly one caller, `AlertControlsMenu`, and it is
 * what settles the Escape-layering question #335 deferred.
 *
 * Every open layer listens on `document`, so one Escape dismisses ALL of them
 * rather than the innermost first. Layering would need a shared layer stack —
 * global mutable state. #336 deleted `AlertControlsMenu`'s hand-rolled
 * `e.stopPropagation()` instead of building one, because that panel closes
 * whenever focus leaves it and therefore can never BE a background layer. Every
 * route into "panel open behind a modal" — tabbing out, clicking another
 * trigger — is already closed by `focusout` or by the outside `mousedown`
 * before Escape is ever pressed. The layering defended an unreachable state.
 *
 * The condition that WOULD force the stack: a layer rendered nested inside
 * another layer's panel, which must win Escape without stopping propagation
 * itself. None exists today. `AlertThresholdField` is not it — it stops the
 * event child-side while a level draft is live, so no `document` listener ever
 * sees that press, and it behaves identically with or without a stack. That
 * child-side guard is also why this hook must stay on `document` in the bubble
 * phase. The resolved outcome is pinned by the `CONTRACT:` test in
 * `SearchInput.dismiss.test.tsx`.
 *
 * The hook owns dismissal only. It never moves focus: where focus should land
 * after a dismiss is the consumer's call (`DashboardToolbar` and
 * `AlertControlsMenu` both return it to the trigger on Escape and deliberately
 * not on an outside pointer), and a hook that stole focus would break both.
 */
import { useEffect, useRef, type RefObject } from 'react';

/**
 * Which interaction dismissed the layer. Values mirror the DOM event names.
 * `'focusout'` is emitted when `dismissOnFocusOut` is enabled.
 *
 * Consumers that restore focus to their trigger must do it on `'escape'` only:
 * pulling focus back after an outside pointer would yank it off whatever the
 * user just clicked. `DashboardToolbar` and `AlertControlsMenu` both make that
 * distinction, which is why the reason is passed rather than inferred.
 */
export type DismissibleLayerReason = 'outside' | 'escape' | 'focusout';

export interface DismissibleLayerOptions {
  /**
   * Whether the layer is currently open. Listeners are registered only while
   * this is `true`, and are removed on close and on unmount.
   */
  open: boolean;
  /**
   * Called when the user dismisses the layer — an outside `mousedown`, Escape,
   * or (when enabled) focus moving outside every supplied ref. Never called
   * while `open` is `false`. A `() => void` callback that ignores the reason is
   * still a valid consumer.
   */
  onDismiss: (reason: DismissibleLayerReason) => void;
  /**
   * Every node that counts as "inside". A `mousedown` within any of them keeps
   * the layer open. Pass the trigger and the panel separately when the panel is
   * portalled away from the trigger, as `Dropdown` does.
   */
  refs: Array<RefObject<HTMLElement | null>>;
  /**
   * When `true`, add a bubble-phase `focusout` listener on `document`.
   * Dismisses with reason `'focusout'` only when `relatedTarget` is non-null
   * and outside every mounted ref. Default `false`.
   *
   * `AlertControlsMenu` is the only consumer that needs this: it closes
   * whenever focus leaves the panel, so the panel can never be a background
   * layer behind a modal. That single property is what lets #336 delete
   * `AlertControlsMenu`'s `e.stopPropagation()` on Escape rather than
   * building the layer stack deferred by #335 — see the hook header.
   */
  dismissOnFocusOut?: boolean;
}

export function useDismissibleLayer({
  open,
  onDismiss,
  refs,
  dismissOnFocusOut = false,
}: DismissibleLayerOptions): void {
  // `refs` is an array literal at every call site, so it has a fresh identity on
  // every render; `onDismiss` usually does too. Feeding either to the effect
  // below would tear the listeners down and rebuild them on every render — the
  // opposite of what this hook exists to fix. Reading both from a ref keeps the
  // effect keyed on `open` (and `dismissOnFocusOut`, a primitive).
  const latest = useRef({ onDismiss, refs });

  // Synced in an effect rather than during render: writing `someRef.current`
  // while rendering trips the React Compiler's react-hooks/refs diagnostic
  // (#141). Handlers only ever read this after every effect has run.
  useEffect(() => {
    latest.current = { onDismiss, refs };
  });

  useEffect(() => {
    if (!open) return;

    const dismissesOn = (target: Node) => {
      // A ref whose node has not mounted yet tells us nothing about where the
      // pointer landed. If NONE of them has a node, we cannot say the click was
      // outside, so we leave the layer alone — matching what all three migrated
      // components did before the hook.
      const mounted = latest.current.refs.filter((ref) => ref.current !== null);
      if (mounted.length === 0) return false;
      return !mounted.some((ref) => ref.current!.contains(target));
    };

    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (!dismissesOn(target)) return;
      latest.current.onDismiss('outside');
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      latest.current.onDismiss('escape');
    };

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);

    let cleanupFocusOut: (() => void) | undefined;
    if (dismissOnFocusOut) {
      const onFocusOut = (event: FocusEvent) => {
        // A null relatedTarget means focus went nowhere focusable — the
        // panel's own padding, or the window losing focus. Neither is a
        // move to another surface and must not dismiss.
        const related = event.relatedTarget as Node | null;
        if (!related) return;
        if (!dismissesOn(related)) return;
        latest.current.onDismiss('focusout');
      };
      document.addEventListener('focusout', onFocusOut);
      cleanupFocusOut = () =>
        document.removeEventListener('focusout', onFocusOut);
    }

    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
      cleanupFocusOut?.();
    };
  }, [open, dismissOnFocusOut]);
}
